import React, { FC, useMemo } from 'react';
import ApplicationDetailsHeader from 'components/ApplicationDetails/ApplicationDetailsHeader';
import Applications from 'components/ApplicationDetails/Applications';
import AdvisorySection from 'components/ApplicationDetails/AdvisorySection';
import { Infobox, Modal, Title } from 'ui-library';
import Icon, { ICON_INFO_OUTLINE, ICON_TYPE_CLOSE } from 'ui-library/components/Icon';
import { useTranslation } from 'react-i18next';
import { useApplicationDetails } from 'datasource/useApplicationDetails';
import { JAUsersType } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { ApplicationTypesEnum } from 'components/ApplicationDetails/Applications/Applications.types';
import { ApplicationStatus, applicationStatusIgnoreListForReUpload, DocStatus } from 'components/DocBox/DocBox.types';
import Preloader from '../../components/Preloader';
import './ApplicationDetails.css';

type TApplicationDetailsProps = {
    is3aVB?: boolean;
}

const ApplicationDetails: FC<TApplicationDetailsProps> = ({
    is3aVB,
}) => {
    const {
        advisoryData, applicationsData, isAdvisoryExpired, isWithdrawalPlanChange,
        proofOfAssetDocuments, advisoryDocument, basicAgreementDocuments, isAdvisoryCase, isApplicationsLoading, showGeneralDocumentsAndProofOfAssetsSection,
        hasDocumentESignAccess, emptyFormDocuments, isJA,
        jaUsers,
        idDocuments,
        isAllIdDocumentsSigned,
        onManualSign,
        onESign,
        onIdDocumentUpload,
        apiError, isFollowUpInvestorProfileCase,
        canReUploadAdvisoryDocs,
        showCanNotSubmitApplicationMessage,
        advisoryDocSubmittedForReview,
        setAdvisoryDocSubmittedForReview,
        canCompleteFollowUpInvestorProfileApplication,
        hasAccessToReUpload,
        onAdvisoryDocReUpload,
    } = useApplicationDetails() || {};
    const { t } = useTranslation();

    const showIdDocuments = useMemo(() => applicationsData?.some(data => data?.typeKey === ApplicationTypesEnum.NEW), [applicationsData]);
    const isAllApplicationDocumentsSigned = useMemo(() => applicationsData?.some(application => application?.documents?.some(doc => doc?.status === DocStatus.ReadyToSubmit)), [applicationsData]);
    const isAllApplicationDocumentsManualSigned = useMemo(() => applicationsData?.some(application => application?.documents?.some(doc => !doc?.isEsigned)), [applicationsData]);
    const isVIOApproved = useMemo(() => applicationsData?.some(application => application?.status === ApplicationStatus.completed), [applicationsData]);
    const isAnyOftheApplicationsInReUploadIgnoreList = applicationsData?.some(application => applicationStatusIgnoreListForReUpload?.includes(application?.status));

    return (
        <>
            <Preloader isLoading={isApplicationsLoading}>
                <div className="application-details">
                    <ApplicationDetailsHeader />
                    {apiError && (
                        <div className="mb-16">
                            <Infobox error icon={ICON_INFO_OUTLINE} className="application-detail--advisory-error">
                                {apiError?.message}
                            </Infobox>
                        </div>
                    )}
                    {advisoryData && !isWithdrawalPlanChange && (
                        <AdvisorySection
                            isAdvisoryCase={isAdvisoryCase}
                            advisoryDocument={advisoryDocument}
                            proofOfAssetDocuments={proofOfAssetDocuments}
                            basicAgreementDocuments={basicAgreementDocuments}
                            data={advisoryData}
                            isExpired={isAdvisoryExpired}
                            isRenderGeneralDocumentsInProgress={false}
                            showGeneralDocumentsAndProofOfAssetsSection={showGeneralDocumentsAndProofOfAssetsSection}
                            hasDocumentESignAccess={hasDocumentESignAccess}
                            isJA={isJA}
                            jaUsers={jaUsers as JAUsersType}
                            onManualSign={onManualSign}
                            onESign={onESign}
                            showIdDocuments={showIdDocuments}
                            idDocuments={idDocuments}
                            onIdDocumentUpload={onIdDocumentUpload}
                            canReUploadAdvisoryDocs={canReUploadAdvisoryDocs}
                            onAdvisoryDocReUpload={onAdvisoryDocReUpload}
                            canReuploadBasicAgreement={!isAnyOftheApplicationsInReUploadIgnoreList}
                        />
                    )}
                    {isAdvisoryExpired && (
                        <Infobox error icon={ICON_INFO_OUTLINE} className="application-detail--advisory-error">
                            {t('ApplicationDetails.AdvisorySection.AdvisoryError')}
                        </Infobox>
                    )}
                    {isAdvisoryCase && advisoryDocument?.status === DocStatus?.Error && (
                        <Infobox error icon={ICON_INFO_OUTLINE} className="application-detail--advisory-error">
                            {t('ApplicationDetails.AdvisorySection.AdvisoryError.NotGenerated')}
                        </Infobox>
                    )}
                    {isFollowUpInvestorProfileCase && isAllApplicationDocumentsManualSigned && isAllApplicationDocumentsSigned && !isVIOApproved && (
                        <Infobox info icon={ICON_INFO_OUTLINE} className="mb-32">
                            {t('ApplicationDetails.FollowUpInvestorProfile.Info')}
                        </Infobox>
                    )}
                    {showCanNotSubmitApplicationMessage ? (
                        <Infobox info icon={ICON_INFO_OUTLINE} className="mb-32">
                            {t('ApplicationDetails.CanNotBeSubmittedDirectly.Info')}
                        </Infobox>
                    ) : ''}
                    <Applications
                        emptyFormDocuments={emptyFormDocuments}
                        applications={applicationsData}
                        isAdvisoryExpired={isAdvisoryExpired || (isAdvisoryCase && !advisoryDocument?.documentId)}
                        is3aVB={is3aVB}
                        hasDocumentESignAccess={hasDocumentESignAccess}
                        isAllIdDocumentsSigned={isAllIdDocumentsSigned}
                        apiError={apiError}
                        canCompleteFollowUpInvestorProfileApplication={canCompleteFollowUpInvestorProfileApplication}
                        hasAccessToReUpload={hasAccessToReUpload}
                    />
                    {apiError && (
                        <div className="mt-16">
                            <Infobox error icon={ICON_INFO_OUTLINE} className="application-detail--advisory-error">
                                {apiError?.message}
                            </Infobox>
                        </div>
                    )}
                </div>
            </Preloader>

            {advisoryDocSubmittedForReview && (
                <Modal
                    title=""
                    visible
                    okText={t('clientDashboard.close')}
                    cancelText=""
                    onOk={() => setAdvisoryDocSubmittedForReview(false)}
                    onCancel={() => setAdvisoryDocSubmittedForReview(false)}
                    okType="primary"
                    width={610}
                    className="upload-document-modal"
                    borderRadius
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
                >
                    <Title type={2} className="font-normal mb-32">
                        {t('ApplicationDetails.AdvisoryDocSubmittedForReview.ThankingModal.Title')}
                    </Title>
                    <p>{t('ApplicationDetails.AdvisoryDocSubmittedForReview.ThankingModal.paraOne')}</p>
                    <p>{t('ApplicationDetails.AdvisoryDocSubmittedForReview.ThankingModal.paraTwo')}</p>
                </Modal>
            )}
        </>
    );
};

export default ApplicationDetails;
